<script lang="ts">
  import { SortableList } from "@jhubbardsf/svelte-sortablejs";
  import {
    sortablePreventDOMManipulation,
    sortablePreventUnmovableItemsFromMoving,
    stopPropagation,
    sortableForceFallbackForFirefox,
  } from "./genericHandlers";
  import {
    existingCardSelected,
    librarySoundSelected,
    soundBoard,
  } from "./store";
  import { CardKinds, PlaySettings } from "./SoundCard";
  import AmbienceCard from "./AmbienceCard.svelte";
  import EffectCard from "./EffectCard.svelte";
  import SceneTitle from "./SceneTitle.svelte";
  import LaneFillerMessage from "./LaneFillerMessage.svelte";
  import CloseSceneButton from "./CloseSceneButton.svelte";
  import GuidedSceneMainControls from "./GuidedSceneMainControls.svelte";
  import GuidedSceneMusicLane from "./GuidedSceneMusicLane.svelte";
  import type { GuidedScene } from "./GuidedScene";
  import { isFocusedScene } from "./global";
  import { notifyWhenMounted, tutorial } from "./tutorial/Tutorial";

  export let scene;
  export let duplicateCallback: (evt) => void;

  let isOpen: boolean = isFocusedScene(scene);

  let laneElement;
  $: if (isOpen) {
    tutorial.notifyDetailsOpen(laneElement)
  }

  let musicLane;
  let isAmbiencePlaying = false;
  let ambienceCards = [];
  let effectCards = [];
  let lastDroppedInto = null;
  let lastDroppedFrom = null;

  let draggingFromDrawer = false;
  librarySoundSelected.subscribe((newVal) => {
    draggingFromDrawer = newVal !== null;
  });

  const handleAdd = (evt, kind) => {
    sortablePreventDOMManipulation(evt);
    if (draggingFromDrawer) {
      soundBoard.UpdateVoid(() => {
        scene.AddCardAtIdx(
          $librarySoundSelected.Name,
          $librarySoundSelected.Data,
          kind,
          evt.newIndex,
        );
      });
    } else {
      const cardId = evt.item.id.replace("soundcard-", "");
      soundBoard.UpdateVoid(() => {
        scene.AddExistingCard($existingCardSelected, kind, evt.newIndex);
      });
    }
    lastDroppedInto = kind;
  };
  const handleRemove = (evt, kind) => {
    sortablePreventDOMManipulation(evt);
    const lane = scene.findLaneByKind(kind);
    const card = lane.GetCardAtIdx(evt.oldIndex);
    soundBoard.UpdateVoid(() => {
      lane.DeleteCard(card.id);
    });
  };
  const handleUpdate = (evt, kind) => {
    sortablePreventDOMManipulation(evt);
    const lane = scene.findLaneByKind(kind);
    soundBoard.UpdateVoid(() => {
      lane.MoveCard(evt.oldIndex, evt.newIndex);
    });
    lastDroppedInto = kind;
  };
  const handleChoose = (evt, kind) => {
    sortablePreventDOMManipulation(evt);
    const lane = scene.findLaneByKind(kind);
    const card = lane.GetCardAtIdx(evt.oldIndex);
    existingCardSelected.set(card);
    lastDroppedFrom = kind;
  };
  const handleStart = (evt, kind) => {
    sortablePreventDOMManipulation(evt);
  };
  const handleEnd = (evt, kind) => {
    sortablePreventDOMManipulation(evt);
    existingCardSelected.set(null);
    // This is run before Svelte has had a chance to update the arrays.
    // We always stop the cards, as they will be re-started in ambiances.
    const stopDroppedCard = () => {
      if (kind === CardKinds.Music) {
        musicLane.stopCardAtIdx(evt.oldIndex);
      } else if (kind === CardKinds.Effect) {
        effectCards[evt.oldIndex].stop();
      } else if (kind === CardKinds.Ambience) {
        ambienceCards[evt.oldIndex].stop();
      }
    };
    if (lastDroppedInto !== lastDroppedFrom) {
      stopDroppedCard();
    }
    lastDroppedInto = null;
    lastDroppedFrom = null;
  };
  const handleNameChangeRequest = (newName) => {
    soundBoard.RenameScene(scene.id, newName);
  };

  const handleChangeSettingValue = (settingId, newValue) => {
    soundBoard.UpdateVoid(() => {
      scene.ChangeSettingValue(settingId, newValue);
    });
  };

  function handleGlobalVolumeChanged(newVolume) {
    soundBoard.UpdateVoid(() => {
      scene.globalVolume = newVolume;
    });
  }

  const handleNameChange = (sceneId, cardId, newName) => {
    soundBoard.RenameCard(sceneId, cardId, newName);
  };

  const handlePlaySettingsChange = (
    sceneId,
    cardId,
    mutator: (PlaySettings) => PlaySettings,
  ) => {
    soundBoard.ChangeCardPlaySettings(sceneId, cardId, mutator);
  };

  const handlePlayAll = () => {
    ambienceCards.forEach((card) => {
      if (card) {
        card.enqueuePlay();
      }
    });
    isAmbiencePlaying = true;
    musicLane.startPlaying();
  };

  const handleStopAll = () => {
    ambienceCards.forEach((card) => {
      if (card) {
        card.stop();
      }
    });
    isAmbiencePlaying = false;
    musicLane.stopAllExcept(undefined);
    effectCards.forEach((card) => {
      if (card) {
        card.stop();
      }
    });
  };
</script>

<article
  bind:this={laneElement}
  class="soundboard-lane editable-icon"
  style={scene.bannerUrl ? `background-image: url("${scene.bannerUrl}");` : ""}
  use:notifyWhenMounted
>
  <CloseSceneButton {scene} onSceneClosing={handleStopAll}></CloseSceneButton>
  <div>
    <details bind:open={isOpen} >
      <summary class="soundboard-lane-header">
        <SceneTitle
          nameChangeCallback={handleNameChangeRequest}
          {duplicateCallback}
          {scene}
        />
        <GuidedSceneMainControls
          {isAmbiencePlaying}
          {handlePlayAll}
          {handleStopAll}
          {scene}
          volumeChangeCallback={handleGlobalVolumeChanged}
          settingsChangedCallback={handleChangeSettingValue}
        />
      </summary>
      <div class="ambience-lane" use:notifyWhenMounted>
        <div class="ambience-header">
          <strong class="light"> Ambience:</strong>
        </div>
        <div on:pointerdown={stopPropagation} on:mousedown={stopPropagation}>
          <SortableList
            class="soundboard-lane-container"
            swapThreshold={0.75}
            animation={250}
            easing="ease-in-out"
            onAdd={(evt) => handleAdd(evt, CardKinds.Ambience)}
            onRemove={(evt) => handleRemove(evt, CardKinds.Ambience)}
            onUpdate={(evt) => handleUpdate(evt, CardKinds.Ambience)}
            onStart={(evt) => handleStart(evt, CardKinds.Ambience)}
            onEnd={(evt) => handleEnd(evt, CardKinds.Ambience)}
            onChoose={(evt) => handleChoose(evt, CardKinds.Ambience)}
            onMove={sortablePreventUnmovableItemsFromMoving}
            forceFallback={sortableForceFallbackForFirefox()}
            handle=".soundcard"
            filter=".unsortable"
            group={{ name: "sounds-on-lanes" }}
            ghostClass="sound-ghost"
          >
            {#if scene.ambiences.cards.length !== 0}
              {#each scene.ambiences.cards as card, idx (card.id)}
                <AmbienceCard
                  {card}
                  {scene}
                  lanePlaying={isAmbiencePlaying}
                  bind:this={ambienceCards[idx]}
                  playSettingsChangedCallback={handlePlaySettingsChange}
                  nameChangeCallback={handleNameChange}
                />
              {/each}
            {:else}
              <LaneFillerMessage
                msg={"Try adding some natural sounds that will immerse players, like a campfire or light rain."}
              />
            {/if}
          </SortableList>
        </div>
      </div>
      <div
        class="music-lane"
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
      >
        <GuidedSceneMusicLane
          bind:this={musicLane}
          {scene}
          {handleAdd}
          {handleRemove}
          {handleUpdate}
          {handleStart}
          {handleEnd}
          {handleChoose}
          {handlePlaySettingsChange}
          {handleNameChange}
        />
      </div>
      <div class="sound-effects-lane">
        <strong class="light sound-effects-header">Effects:</strong>
        <div on:pointerdown={stopPropagation} on:mousedown={stopPropagation}>
          <SortableList
            class="soundboard-lane-container"
            swapThreshold={0.75}
            animation={250}
            easing="ease-in-out"
            onAdd={(evt) => handleAdd(evt, CardKinds.Effect)}
            onRemove={(evt) => handleRemove(evt, CardKinds.Effect)}
            onUpdate={(evt) => handleUpdate(evt, CardKinds.Effect)}
            onStart={(evt) => handleStart(evt, CardKinds.Effect)}
            onEnd={(evt) => handleEnd(evt, CardKinds.Effect)}
            onChoose={(evt) => handleChoose(evt, CardKinds.Effect)}
            onMove={sortablePreventUnmovableItemsFromMoving}
            forceFallback={sortableForceFallbackForFirefox()}
            handle=".soundcard"
            filter=".unsortable"
            group={{ name: "sounds-on-lanes" }}
            ghostClass="sound-ghost"
          >
            {#if scene.effects.cards.length !== 0}
              {#each scene.effects.cards as card, idx (card.id)}
                <EffectCard
                  {card}
                  {scene}
                  bind:this={effectCards[idx]}
                  nameChangeCallback={handleNameChange}
                />
              {/each}
            {:else}
              <LaneFillerMessage
                msg={"Search for one-off sounds that will cause a reaction, like a bone-chilling thunder or a nice birdsong."}
              />
            {/if}
          </SortableList>
        </div>
      </div>
    </details>
  </div>
</article>
